import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp } from 'react-icons/fa';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import ContactForm from '../components/ui/ContactForm';
import FAQAccordion from '../components/ui/FAQAccordion';
import PageHero from '../components/ui/PageHero';
import { contactInfo } from '../data/content';
import { faqs } from '../data/content';

const contactItems = [
  { icon: FaPhone, label: 'Phone', value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
  { icon: FaEnvelope, label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` },
  { icon: FaMapMarkerAlt, label: 'Address', value: contactInfo.address, href: null },
  { icon: FaClock, label: 'Business Hours', value: contactInfo.hours, href: null },
];

export default function ContactPage() {
  return (
    <main className="w-full overflow-x-hidden pt-16 lg:pt-20">
      {/* Hero */}
      <PageHero
        badge="Contact Us"
        title="Get in Touch With Our Experts"
        subtitle="Have a question or need a free consultation? Our team is ready to help you navigate your compliance, legal, and technology needs."
      />

      {/* Contact section */}
      <section className="section-pad section-bg-alt">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Contact info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>Contact Information</h2>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Reach us through any of the following channels. We typically respond within a few hours.</p>
              </div>
              {contactItems.map(({ icon: Icon, label, value, href }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="card p-5 flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white" style={{ background: 'var(--gradient-primary)' }}>
                    <Icon />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-secondary)' }}>{label}</p>
                    {href ? (
                      <a href={href} className="text-sm font-medium hover:underline" style={{ color: 'var(--color-text)' }}>{value}</a>
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--color-text)' }}>{value}</p>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* WhatsApp CTA */}
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-5 rounded-2xl font-semibold text-white transition-all duration-200 hover:opacity-90"
                style={{ background: '#25D366' }}
              >
                <FaWhatsapp className="text-2xl" />
                <div>
                  <p className="font-bold">Chat on WhatsApp</p>
                  <p className="text-sm text-white/80">Quick replies during business hours</p>
                </div>
              </a>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="card p-8">
                <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>Send Us a Message</h2>
                <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>Fill in the form below and we'll get back to you within 24 hours.</p>
                <ContactForm />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Map placeholder */}
      <section className="section-pad" style={{ background: 'var(--color-surface)' }}>
        <Container>
          <SectionHeading badge="Our Location" title="Find Us" subtitle="Visit us at our office or connect remotely — we serve clients across all of India." />
          <div
            className="w-full h-72 md:h-96 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--color-surface-alt)', border: '2px dashed var(--color-border)' }}
          >
            <div className="text-center">
              <FaMapMarkerAlt className="text-5xl mx-auto mb-3" style={{ color: 'var(--color-secondary)' }} />
              <p className="font-semibold" style={{ color: 'var(--color-text)' }}>Navi Mumbai, Maharashtra</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{contactInfo.address}</p>
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="section-pad section-bg-alt">
        <Container>
          <SectionHeading badge="FAQs" title="Frequently Asked Questions" subtitle="Quick answers to common questions about our services." />
          <div className="w-full mx-auto">
            <FAQAccordion items={faqs.slice(0, 5)} />
          </div>
        </Container>
      </section>
    </main>
  );
}
